import { useEffect, useState } from "react";
import i18n from "@/i18n";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ne";
import "dayjs/locale/hi";
import enUS from "antd/locale/en_US";
import hiIN from "antd/locale/hi_IN";

// AntD's Nepali locale (ne_NP) ships in v5.10+. We attempt a dynamic
// import at module-init time; if it resolves we cache and use it.
// Falls back to en_US so users see English month names but the rest
// of the app remains in Nepali. Drop this conditional once AntD
// upgrades have been confirmed in CI.
//
// ESM note: require() is unavailable here ("type": "module").
// A top-level dynamic import is the cleanest ESM-compatible way to
// probe for an optionally-present package.
let neNP: typeof enUS = enUS;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import("antd/locale/ne_NP" as any)
  .then((mod) => {
    neNP = (mod.default ?? mod) as typeof enUS;
  })
  .catch(() => {
    // ne_NP not available in this AntD version — keep en_US fallback.
  });

const ANTD_LOCALES = { en: enUS, ne: neNP, hi: hiIN } as const;
const DAYJS_LOCALES = { en: "en", ne: "ne", hi: "hi" } as const;

type SupportedLanguage = keyof typeof ANTD_LOCALES;

function normalize(lang: string): SupportedLanguage {
  const head = lang.split("-")[0].toLowerCase();
  if (head === "ne" || head === "hi") return head;
  return "en";
}

/**
 * Single-source-of-truth hook for third-party locale sync. Mount once at
 * the layout root (ThemeProvider). Whenever i18n.language changes:
 *   - dayjs.locale(…) → date displays flip language
 *   - returned `antdLocale` flips → consumer plugs it into ConfigProvider
 */
export function useSyncThirdPartyLocale() {
  const initial = normalize(i18n.language);
  const [antdLocale, setAntdLocale] = useState(ANTD_LOCALES[initial]);

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALES[normalize(i18n.language)]);
    setAntdLocale(ANTD_LOCALES[normalize(i18n.language)]);

    const onChange = (lng: string) => {
      const head = normalize(lng);
      dayjs.locale(DAYJS_LOCALES[head]);
      setAntdLocale(ANTD_LOCALES[head]);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  return { antdLocale };
}
